// src/Website/Components/JobsHero.jsx
//
// Hero for the "Jobs" product marketing page (/products/jobs).
//
// Scene: headline/CTAs on the left, a 3D-tilted translucent "hologram"
// panel (inner pipeline rail + AI match results), and the WebIdeal robot
// mascot pointing at it.
//
// ── Choreographed loop ──────────────────────────────────────────────
//   idle → detect user → read profile → point to screen → hologram
//   activates → scan jobs → matches appear → highlight best match →
//   look at result → back to idle
//
// The robot is a flat PNG, so its "fingertip glow" is a positioned bloom
// overlay (see FINGER_TIP) and its body is animated with lean/scale.
// Honours prefers-reduced-motion by holding a static, populated state.

import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import {
  Search,
  ArrowRight,
  Sparkles,
  Bookmark,
  ScanSearch,
  BadgeCheck,
  BarChart3,
  Send,
  UserRound,
} from "lucide-react";

import { PageContainer, TechLabel, colors as DS } from "../../design-system";

const ACCENT = DS.product.jobs; // #0044CC
const GREEN = "#16a34a";
const CYAN = "#5ee7ff";
const NAVY = "#0f2744";
const MUTED = "#8494a6";
const LAVENDER = "#eef1ff";
const ICON_BLUE = "#4f6bd8";

const TILT = "rotateY(17deg) rotateX(2deg)";

/* ── Bloom overlay position on the robot PNG (percentages of its box) ── */
const FINGER_TIP = { left: "7%", top: "30%" };

/* ─────────────── Animation timeline ─────────────── */
const PHASES = [
  "idle",
  "detect",
  "eyes",
  "point",
  "hologram",
  "scan",
  "matches",
  "highlight",
  "look",
];

const DURATION = {
  idle: 1800,
  detect: 900,
  eyes: 800,
  point: 900,
  hologram: 900,
  scan: 1900,
  matches: 1700,
  highlight: 1700,
  look: 1200,
};

// Status caption shown under the panel header per phase
const STATUS = {
  idle: "Assistant ready",
  detect: "Candidate detected…",
  eyes: "Reading your profile…",
  point: "Opening job hologram…",
  hologram: "Connecting to job graph…",
  scan: "Scanning millions of jobs…",
  matches: "3 strong matches found",
  highlight: "Best match: Product Designer",
  look: "Delivering your results",
};

// Which rail step is lit during each phase (-1 = none)
const RAIL_STEP = {
  idle: -1,
  detect: 0,
  eyes: 0,
  point: 0,
  hologram: 0,
  scan: 1,
  matches: 2,
  highlight: 3,
  look: 4,
};

/** Advances through PHASES on a loop. */
function usePhaseLoop(enabled) {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (!enabled) return undefined;
    const ms = DURATION[PHASES[idx]] ?? 1200;
    const t = setTimeout(() => setIdx((p) => (p + 1) % PHASES.length), ms);
    return () => clearTimeout(t);
  }, [idx, enabled]);

  return idx;
}

/* ── Pipeline steps ── */
const PIPELINE = [
  { icon: UserRound, label: "Understanding", sub: "your profile" },
  { icon: ScanSearch, label: "Scanning", sub: "millions of jobs" },
  { icon: BadgeCheck, label: "Matching", sub: "perfect opportunities" },
  { icon: BarChart3, label: "Ranking", sub: "best matches" },
  { icon: Send, label: "Delivering", sub: "top results" },
];

/* ── Match results ── */
const MATCHES = [
  {
    role: "Product Designer",
    meta: "UI/UX Design • Bengaluru",
    tags: ["Figma", "User Research", "Design System"],
    match: 95,
  },
  {
    role: "Frontend Developer",
    meta: "React.js • Hyderabad",
    tags: ["React", "TypeScript", "Tailwind"],
    match: 92,
  },
  {
    role: "Data Analyst",
    meta: "SQL • Power BI • Remote",
    tags: ["SQL", "Power BI", "Python"],
    match: 89,
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: i * 0.08, ease: [0.25, 0.1, 0.25, 1] },
  }),
};

/* ─────────── Oscilloscope waveform ─────────── */
function Waveform({ active }) {
  const pts = [
    0, 12, 6, 12, 10, 9, 14, 15, 18, 11, 22, 13, 26, 6, 30, 18, 34, 2, 38, 21,
    42, 8, 46, 14, 50, 10, 54, 16, 58, 4, 62, 19, 66, 9, 70, 13, 74, 11, 78, 15,
    82, 7, 86, 17, 90, 10, 94, 13, 98, 11, 102, 14, 106, 9, 110, 15, 114, 12,
    118, 13, 122, 10, 126, 14, 130, 12, 136, 12, 142, 12, 150, 12,
  ];
  const path = pts.reduce((acc, v, i) => {
    if (i % 2 !== 0) return acc;
    return `${acc}${i === 0 ? "M" : "L"}${v} ${pts[i + 1]} `;
  }, "");

  return (
    <svg viewBox="0 0 150 24" className="w-full h-5" preserveAspectRatio="none" aria-hidden="true">
      {/* flat baseline when inactive */}
      <path d="M0 12 L150 12" fill="none" stroke={`${ACCENT}33`} strokeWidth="0.7" />
      <motion.path
        d={path}
        fill="none"
        stroke={`${ACCENT}bb`}
        strokeWidth="0.9"
        strokeLinejoin="round"
        initial={false}
        animate={{ opacity: active ? 1 : 0, pathLength: active ? 1 : 0.05 }}
        transition={{ duration: 0.7, ease: "easeOut" }}
      />
    </svg>
  );
}

/* ─────────── Skeleton row (shown while scanning) ─────────── */
function SkeletonRow() {
  return (
    <div
      className="bg-white/70 rounded-xl border px-3 py-2.5 flex items-center gap-3 overflow-hidden"
      style={{ borderColor: "#eef2f7" }}
    >
      <div className="w-9 h-9 rounded-lg flex-shrink-0" style={{ background: "#e8edf6" }} />
      <div className="flex-1 min-w-0 space-y-1.5">
        <div className="h-2 rounded w-1/2" style={{ background: "#e8edf6" }} />
        <div className="h-1.5 rounded w-1/3" style={{ background: "#eef2f7" }} />
        <div className="h-1.5 rounded w-2/3" style={{ background: "#f2f5fa" }} />
      </div>
      {/* shimmer sweep */}
      <motion.div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(100deg, transparent 20%, rgba(255,255,255,0.75) 50%, transparent 80%)",
        }}
        animate={{ x: ["-120%", "120%"] }}
        transition={{ duration: 1.1, repeat: Infinity, ease: "linear" }}
      />
    </div>
  );
}

/* ─────────── Match result card ─────────── */
function MatchCard({ item, index, isBest, highlight }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10, scale: 0.98 }}
      animate={{
        opacity: 1,
        y: 0,
        scale: isBest && highlight ? 1.02 : 1,
        boxShadow:
          isBest && highlight
            ? `0 0 0 2px ${GREEN}, 0 8px 22px -8px ${GREEN}66`
            : "0 1px 2px rgba(16,40,80,0.05)",
      }}
      exit={{ opacity: 0, y: -6 }}
      transition={{
        duration: 0.45,
        delay: index * 0.16,
        ease: [0.25, 0.1, 0.25, 1],
      }}
      className="bg-white rounded-xl border px-3 py-2.5 flex items-center gap-3 relative"
      style={{ borderColor: "#eef2f7" }}
    >
      <div
        className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
        style={{ background: LAVENDER, color: ICON_BLUE }}
      >
        <UserRound className="w-[18px] h-[18px]" strokeWidth={2} />
      </div>

      <div className="flex-1 min-w-0">
        <h4 className="text-[13px] font-bold leading-tight truncate" style={{ color: NAVY }}>
          {item.role}
        </h4>
        <p className="text-[10px] mt-[3px] truncate" style={{ color: MUTED }}>
          {item.meta}
        </p>
        <div className="flex flex-wrap gap-1 mt-1.5">
          {item.tags.map((t) => (
            <span
              key={t}
              className="text-[8px] font-medium px-1.5 py-[2px] rounded"
              style={{ background: "#f1f4f9", color: "#6b7a8d" }}
            >
              {t}
            </span>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-2 flex-shrink-0">
        <div className="text-right leading-tight">
          <div className="text-[13px] font-extrabold" style={{ color: GREEN }}>
            {item.match}%
          </div>
          <div className="text-[9px] font-semibold" style={{ color: GREEN }}>
            Match
          </div>
        </div>
        <Bookmark
          className="w-4 h-4"
          strokeWidth={1.8}
          style={{ color: ACCENT }}
          fill={isBest && highlight ? ACCENT : "none"}
        />
      </div>

      {/* "Best match" flag */}
      <AnimatePresence>
        {isBest && highlight && (
          <motion.span
            initial={{ opacity: 0, scale: 0.8, y: 4 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8 }}
            className="absolute -top-2 left-3 text-[7.5px] font-bold uppercase tracking-wide px-1.5 py-[2px] rounded-full text-white"
            style={{ background: GREEN, letterSpacing: "0.1em" }}
          >
            Best match
          </motion.span>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

/* ═══════════════════════ HERO ═══════════════════════ */
export default function JobsHero({ ctaPrimary, ctaSecondary }) {
  const reduce = useReducedMotion();
  const liveIdx = usePhaseLoop(!reduce);

  // With reduced motion, hold the fully-populated "highlight" state.
  const idx = reduce ? PHASES.indexOf("highlight") : liveIdx;
  const phase = PHASES[idx];
  const gte = (name) => idx >= PHASES.indexOf(name);

  const holoOn = gte("hologram");
  const scanning = phase === "scan";
  const showMatches = gte("matches");
  const highlightBest = gte("highlight");
  const pointing = gte("point");
  const activeStep = RAIL_STEP[phase];

  // Robot body pose per phase
  const robotPose = {
    idle: { rotate: 0, scale: 1, x: 0 },
    detect: { rotate: 0, scale: 1.02, x: 0 },
    eyes: { rotate: 0, scale: 1.02, x: 0 },
    point: { rotate: -1.6, scale: 1.03, x: -6 },
    hologram: { rotate: -1.6, scale: 1.03, x: -6 },
    scan: { rotate: -1.2, scale: 1.02, x: -4 },
    matches: { rotate: -1, scale: 1.02, x: -4 },
    highlight: { rotate: -0.4, scale: 1.02, x: -2 },
    look: { rotate: 1.6, scale: 1.01, x: 0 },
  }[phase];

  return (
    <section
      className="relative overflow-hidden border-b"
      style={{
        borderColor: DS.hairline,
        background: "linear-gradient(115deg, #ffffff 0%, #fbfdff 45%, #f4f8fe 100%)",
      }}
    >
      <div
        aria-hidden="true"
        className="absolute -top-32 right-[10%] w-[720px] h-[720px] rounded-full opacity-[0.07] blur-3xl pointer-events-none"
        style={{ background: ACCENT }}
      />

      <PageContainer className="relative py-12 md:py-16 lg:min-h-[600px] flex items-center">
        <div className="w-full grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-4 items-center">

          {/* ───────────── LEFT: COPY ───────────── */}
          <motion.div
            className="lg:col-span-4 relative z-40"
            initial="hidden"
            animate="show"
            variants={{ show: { transition: { staggerChildren: 0.08 } } }}
          >
            <motion.div variants={fadeUp} custom={0}>
              <span className="inline-flex items-center gap-1.5" style={{ color: ACCENT }}>
                <Sparkles className="w-3.5 h-3.5" />
                <TechLabel>AI-Powered Job Portal</TechLabel>
              </span>
            </motion.div>

            <motion.h1
              variants={fadeUp}
              custom={1}
              className="mt-5 text-5xl md:text-6xl xl:text-[3.6rem] font-extrabold leading-[1.05] tracking-tight"
              style={{ color: DS.forest }}
            >
              Jobs that
              <br />
              find you,
              <br />
              <span style={{ color: `${DS.forest}73` }}>not the other</span>
              <br />
              <span style={{ color: `${DS.forest}73` }}>way round.</span>
            </motion.h1>

            <motion.p
              variants={fadeUp}
              custom={2}
              className="mt-6 text-sm md:text-[15px] max-w-xs leading-relaxed"
              style={{ color: DS.grid }}
            >
              Our AI understands your skills, matches the right opportunities,
              and helps you get hired faster. All at{" "}
              <strong style={{ color: DS.forest }}>11pm</strong>.
            </motion.p>

            <motion.div variants={fadeUp} custom={3} className="mt-7 flex flex-wrap items-center gap-3">
              <Link to={ctaPrimary?.href || "/jobs"}>
                <button
                  type="button"
                  className="ds-skeu-btn-primary cursor-pointer h-11 px-5 text-sm font-bold inline-flex items-center gap-2"
                >
                  <Search className="w-4 h-4" />
                  {ctaPrimary?.label || "Browse jobs"}
                </button>
              </Link>

              <Link to={ctaSecondary?.href || "/jobs/auth"}>
                <button
                  type="button"
                  className="cursor-pointer h-11 px-5 text-sm font-bold border bg-white inline-flex items-center gap-2 rounded-md transition-colors duration-150 hover:bg-[#1A3C2B]/[0.04]"
                  style={{ borderColor: DS.forest, color: DS.forest }}
                >
                  {ctaSecondary?.label || "Create profile"}
                  <ArrowRight className="w-4 h-4" />
                </button>
              </Link>
            </motion.div>
          </motion.div>

          {/* ───────────── RIGHT: HOLOGRAM PANEL + ROBOT ───────────── */}
          <div className="lg:col-span-8 relative" style={{ perspective: "1500px" }}>
            <div className="relative flex items-center lg:pr-[190px] xl:pr-[240px]">

              {/* ══ Hologram panel ══ */}
              <motion.div
                initial={{ opacity: 0, y: 28 }}
                animate={{
                  opacity: 1,
                  y: 0,
                  scale: holoOn ? 1 : 0.985,
                  boxShadow: holoOn
                    ? `0 30px 60px -22px ${ACCENT}3d, 0 0 0 1px ${CYAN}55, 0 0 34px -6px ${CYAN}59`
                    : `0 18px 40px -22px ${ACCENT}2e`,
                }}
                transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
                className="relative z-10 w-full rounded-[22px] border p-3.5 md:p-4 overflow-hidden"
                style={{
                  borderColor: "rgba(255,255,255,0.85)",
                  background:
                    "linear-gradient(150deg, rgba(255,255,255,0.82) 0%, rgba(233,242,255,0.7) 100%)",
                  backdropFilter: "blur(16px)",
                  WebkitBackdropFilter: "blur(16px)",
                  transform: TILT,
                  transformStyle: "preserve-3d",
                }}
              >
                {/* hologram boot-up flash */}
                <AnimatePresence>
                  {phase === "hologram" && (
                    <motion.div
                      aria-hidden="true"
                      className="absolute inset-0 pointer-events-none z-30"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: [0, 0.55, 0] }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.85, ease: "easeOut" }}
                      style={{ background: `linear-gradient(180deg, ${CYAN}55, transparent 70%)` }}
                    />
                  )}
                </AnimatePresence>

                {/* scan sweep line */}
                <AnimatePresence>
                  {scanning && (
                    <motion.div
                      aria-hidden="true"
                      className="absolute left-0 right-0 h-16 pointer-events-none z-20"
                      initial={{ top: "-12%", opacity: 0 }}
                      animate={{ top: ["-12%", "100%"], opacity: [0, 1, 1, 0] }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 1.8, ease: "linear" }}
                      style={{
                        background: `linear-gradient(180deg, transparent, ${CYAN}3d 55%, ${CYAN}cc 82%, transparent)`,
                      }}
                    />
                  )}
                </AnimatePresence>

                <div className="flex gap-3.5 relative z-10">

                  {/* ── Inner pipeline rail ── */}
                  <div
                    className="hidden sm:block relative flex-shrink-0 pr-3.5"
                    style={{ borderRight: "1px solid rgba(15,39,68,0.07)" }}
                  >
                    <span
                      aria-hidden="true"
                      className="absolute left-[15px] top-5 bottom-5 border-l border-dashed"
                      style={{ borderColor: `${GREEN}59` }}
                    />
                    <div className="flex flex-col gap-3.5 relative">
                      {PIPELINE.map((p, i) => {
                        const Icon = p.icon;
                        const done = activeStep > i;
                        const live = activeStep === i;
                        return (
                          <div key={p.label} className="flex items-center gap-2 relative">
                            <motion.div
                              className="w-[31px] h-[31px] rounded-full flex items-center justify-center flex-shrink-0 relative z-10 bg-white"
                              animate={{
                                borderColor: live || done ? GREEN : "rgba(0,0,0,0)",
                                scale: live ? 1.12 : 1,
                                boxShadow: live ? `0 0 0 4px ${GREEN}22` : "0 1px 3px rgba(16,40,80,0.08)",
                              }}
                              transition={{ duration: 0.35 }}
                              style={{ border: "1.5px solid rgba(0,0,0,0)" }}
                            >
                              <motion.span
                                className="w-[23px] h-[23px] rounded-full flex items-center justify-center"
                                animate={{
                                  background: live ? `${GREEN}1f` : LAVENDER,
                                  color: live ? GREEN : ICON_BLUE,
                                }}
                                transition={{ duration: 0.35 }}
                              >
                                <Icon className="w-3 h-3" strokeWidth={2.2} />
                              </motion.span>
                              {/* pulse ring on the live step */}
                              {live && (
                                <motion.span
                                  aria-hidden="true"
                                  className="absolute inset-0 rounded-full"
                                  style={{ border: `1.5px solid ${GREEN}` }}
                                  animate={{ scale: [1, 1.55], opacity: [0.7, 0] }}
                                  transition={{ duration: 1.1, repeat: Infinity, ease: "easeOut" }}
                                />
                              )}
                            </motion.div>
                            <div className="leading-tight whitespace-nowrap">
                              <motion.div
                                className="text-[10.5px] font-bold"
                                animate={{ color: live ? GREEN : NAVY }}
                                transition={{ duration: 0.3 }}
                              >
                                {p.label}
                              </motion.div>
                              <div className="text-[8.5px] mt-[1px]" style={{ color: MUTED }}>
                                {p.sub}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* ── Main content column ── */}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-[12.5px] font-bold" style={{ color: NAVY }}>
                      AI is finding the best matches for you
                    </h3>

                    {/* live status caption */}
                    <div className="h-3 mt-0.5 mb-0.5 overflow-hidden">
                      <AnimatePresence mode="wait">
                        <motion.p
                          key={phase}
                          initial={{ opacity: 0, y: 6 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -6 }}
                          transition={{ duration: 0.25 }}
                          className="text-[8.5px] font-semibold"
                          style={{ color: scanning ? ACCENT : MUTED }}
                        >
                          {STATUS[phase]}
                        </motion.p>
                      </AnimatePresence>
                    </div>

                    {/* waveform + Live pill */}
                    <div className="flex items-center gap-2 mt-1 mb-2.5">
                      <div className="flex-1 min-w-0">
                        <Waveform active={holoOn} />
                      </div>
                      <motion.span
                        className="inline-flex items-center gap-1 text-[8.5px] font-semibold px-2 py-[3px] rounded-full bg-white border flex-shrink-0"
                        animate={{ opacity: holoOn ? 1 : 0.45 }}
                        style={{ color: GREEN, borderColor: `${GREEN}40` }}
                      >
                        <motion.span
                          className="w-1.5 h-1.5 rounded-full"
                          style={{ background: GREEN }}
                          animate={{ opacity: holoOn ? [1, 0.3, 1] : 0.4 }}
                          transition={{ duration: 1.2, repeat: Infinity }}
                        />
                        Live
                      </motion.span>
                    </div>

                    {/* match cards / skeletons */}
                    <div className="flex flex-col gap-2 relative">
                      <AnimatePresence mode="popLayout">
                        {showMatches
                          ? MATCHES.map((m, i) => (
                              <MatchCard
                                key={m.role}
                                item={m}
                                index={i}
                                isBest={i === 0}
                                highlight={highlightBest}
                              />
                            ))
                          : [0, 1, 2].map((i) => (
                              <motion.div
                                key={`sk-${i}`}
                                className="relative overflow-hidden rounded-xl"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.3, delay: i * 0.06 }}
                              >
                                <SkeletonRow />
                              </motion.div>
                            ))}
                      </AnimatePresence>
                    </div>

                    {/* View all matches */}
                    <div className="mt-2.5 flex justify-center">
                      <motion.button
                        type="button"
                        className="bg-white rounded-lg px-5 py-2 text-[11px] font-bold inline-flex items-center gap-1.5 border"
                        animate={{ opacity: showMatches ? 1 : 0.4 }}
                        transition={{ duration: 0.4 }}
                        style={{
                          color: NAVY,
                          borderColor: "#eef2f7",
                          boxShadow: "0 1px 3px rgba(16,40,80,0.06)",
                        }}
                      >
                        View all matches
                        <ArrowRight className="w-3 h-3" style={{ color: ACCENT }} />
                      </motion.button>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* ══ Robot mascot ══ */}
            <div className="hidden lg:block absolute right-0 xl:-right-6 bottom-0 z-30 pointer-events-none w-[300px] xl:w-[380px]">
              {/* gentle idle float wrapper */}
              <motion.div
                animate={reduce ? undefined : { y: [0, -7, 0] }}
                transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
              >
                {/* phase pose wrapper */}
                <motion.div
                  className="relative"
                  animate={robotPose}
                  transition={{ duration: 0.7, ease: [0.25, 0.1, 0.25, 1] }}
                  style={{ transformOrigin: "60% 90%" }}
                >
                  <img
                    src="/image.png"
                    alt="WebIdeal AI assistant robot"
                    className="w-full h-auto object-contain relative z-10"
                    style={{ filter: "drop-shadow(0 20px 30px rgba(16,40,80,0.18))" }}
                  />

                  {/* fingertip glow + emitted pulse toward the panel */}
                  <motion.span
                    aria-hidden="true"
                    className="absolute z-20 rounded-full pointer-events-none"
                    style={{
                      ...FINGER_TIP,
                      width: "9%",
                      height: "7%",
                      transform: "translate(-50%, -50%)",
                      background: `radial-gradient(circle, #ffffff 0%, ${CYAN} 35%, transparent 70%)`,
                      filter: "blur(4px)",
                      mixBlendMode: "screen",
                    }}
                    animate={{
                      opacity: pointing ? [0.6, 1, 0.6] : 0,
                      scale: pointing ? [0.9, 1.25, 0.9] : 0.7,
                    }}
                    transition={{ duration: 1.3, repeat: Infinity, ease: "easeInOut" }}
                  />

                  {/* energy pulse travelling from fingertip to the panel */}
                  <AnimatePresence>
                    {phase === "hologram" && (
                      <motion.span
                        aria-hidden="true"
                        className="absolute z-20 rounded-full pointer-events-none"
                        style={{
                          ...FINGER_TIP,
                          width: "5%",
                          height: "4%",
                          background: CYAN,
                          filter: "blur(3px)",
                          mixBlendMode: "screen",
                        }}
                        initial={{ opacity: 0, x: 0, scale: 0.6 }}
                        animate={{ opacity: [0, 1, 0], x: [-4, -90], scale: [0.6, 1.4] }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.85, ease: "easeOut" }}
                      />
                    )}
                  </AnimatePresence>
                </motion.div>
              </motion.div>

              {/* contact shadow */}
              <div
                aria-hidden="true"
                className="mx-auto -mt-3 w-[62%] h-5 rounded-[50%] blur-lg"
                style={{ background: "rgba(16,40,80,0.16)" }}
              />
            </div>
          </div>

        </div>
      </PageContainer>
    </section>
  );
}
